import { ChangeDetectorRef, Component, Injector, OnDestroy, Signal, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
   LcsAdvancedMultiSelectDatasource,
   LcsAdvancedMultiSelectOpenDialogEvent,
   LcsAdvancedMultiSelectReadonlyStateType,
   LcsAdvancedMultiSelectStateEventPayloads,
   LcsAdvancedMultiSelectStateInitEventPayload,
   LcsAdvancedMultiSelectTriggerConfig,
   LcsAdvancedMultiSelectTriggerFormGroup,
} from '@lcs/shared/advanced-multi-select';
import { LcsSelectWithAllDisplayTypes } from '@lcs/shared/select-with-all';
import { Store } from '@ngrx/store';
import { ExtensionTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { extensionQueueOptionSelectors } from 'src/app/common-components/entity-select/extension/state/extension-option-reducers/extension-queue-option.reducer';
import { extensionLoadQueuesActions } from 'src/app/common-components/entity-select/extension/state/extension-option.actions';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { ReportParameterComponent } from '../../report-parameter-list/report-parameter-component';
import { ReportParameterConfig } from '../../report-parameter-list/report-parameter.config';
import { SelectedAccountService } from './selected-account-service';

@Component({
   providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: CallQueueExtensionsReportParameterComponent }],
   selector: 'ndt-call-queue-extensions-report-parameter',
   templateUrl: './call-queue-extensions-report-parameter.component.html',
   styleUrls: ['./call-queue-extensions-report-paramter.component.scss'],
   standalone: false,
})
export class CallQueueExtensionsReportParameterComponent implements OnDestroy, ReportParameterComponent {
   form: UntypedFormGroup;

   selectedAccountId: number | null;

   searchControl = new UntypedFormControl('');

   private destroyed$ = new Subject<void>();

   config: ReportParameterConfig;

   extensionPanelDisabled: boolean = false;

   entityName = 'Call Queues';

   readonly store = inject(Store);
   readonly cd = inject(ChangeDetectorRef);
   readonly injector = inject(Injector);
   readonly router = inject(Router);

   readonly allExtensions = this.store.selectSignal(extensionQueueOptionSelectors.selectAll);
   readonly extensions = this.store.selectSignal(extensionQueueOptionSelectors.selectFilteredItems);
   readonly extensionsCallState = this.store.selectSignal(extensionQueueOptionSelectors.selectCallState);

   // Store toSignal results outside of computed
   private _extensionItemsSignal = signal<Signal<ExtensionOption[]> | null>(null);
   private _allSignal = signal<Signal<boolean> | null>(null);

   readonly extensionItemsFromControl = computed(
      () => {
         const sig = this._extensionItemsSignal();
         return sig ? sig() : [];
      },
      { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
   );

   readonly allFromControl = computed(() => {
      const sig = this._allSignal();
      return sig ? sig() : false;
   });

   private updateCallQueueExtensionItemsSignal() {
      const extensionItemsControl = this.form?.controls?.extensions;
      if (extensionItemsControl) {
         const sig = toSignal<ExtensionOption[]>(
            extensionItemsControl.valueChanges.pipe(map(() => extensionItemsControl.getRawValue() ?? [])),
            {
               initialValue: extensionItemsControl.getRawValue() ?? [],
               injector: this.injector,
            }
         ) as Signal<ExtensionOption[]>;
         this._extensionItemsSignal.set(sig);
      } else {
         this._extensionItemsSignal.set(null);
      }
   }

   private updateAllSignal() {
      const allControl = this.form?.controls?.all;
      if (allControl) {
         const sig = toSignal(allControl.valueChanges.pipe(map(() => allControl.getRawValue() ?? false)), {
            initialValue: allControl.getRawValue() ?? false,
            injector: this.injector,
         });
         this._allSignal.set(sig);
      } else {
         this._allSignal.set(null);
      }
   }

   readonly triggerConfig = computed<LcsAdvancedMultiSelectTriggerConfig<ExtensionOption>>(() => {
      return {
         displayOptions: {
            label: this.entityName,
            displayType: LcsSelectWithAllDisplayTypes.Label,
            displayWithFn: (
               items: ExtensionOption[],
               state: LcsAdvancedMultiSelectReadonlyStateType<ExtensionOption>
            ) => {
               const options = this.extensionItemsFromControl() ?? [];
               if (options.length === 1 && options[0].name) {
                  return `${options[0].number} - ${options[0].name}`;
               }
               return `${options.length} ${this.entityName}`;
            },
            hasAllCheckbox: true,
         },
         group: new LcsAdvancedMultiSelectTriggerFormGroup<ExtensionOption>(
            {
               items: new FormControl(this.extensionItemsFromControl() ?? [], { nonNullable: true }),
               all: new FormControl(this.allFromControl(), { nonNullable: true }),
            },
            {
               validators: this.form.hasValidator(Validators.required) ? [Validators.required] : [],
            }
         ),
         syncOn: 'save',
      };
   });

   readonly selectedExtensions = signal<ExtensionOption[]>([]);

   readonly dataSource = computed<LcsAdvancedMultiSelectDatasource<ExtensionOption>>(() => {
      return {
         id: 1,
         label: this.entityName,
         items: this.availableExtensions() ?? [],
         selectedItems: this.resolvedSelectedExtensions(),
         itemTrackBy: this.extensionsTrackBy,
         displayOptions: {
            displayWithFn: (item: ExtensionOption) => {
               return item.name ?? '';
            },
         },
         callState: this.extensionsCallState(),
      };
   });
   readonly extensionById = computed(() => {
      const map = new Map<number, ExtensionOption>();
      for (const ext of this.availableExtensions() ?? []) {
         map.set(ext.extensionId, ext);
      }
      return map;
   });
   readonly resolvedSelectedExtensions = computed<ExtensionOption[]>(() => {
      const lookup = this.extensionById();
      const selected = this.selectedExtensions() ?? [];

      return selected
         .map((sel) => lookup.get(sel.extensionId) ?? sel)
         .filter((ext) => ext.name !== undefined || ext.number !== undefined);
   });

   readonly availableExtensions = computed(() => {
      const Extensions = this.extensions();
      return Extensions;
   });

   constructor(private selectedAccountService: SelectedAccountService, store: Store) {
      this.form = new UntypedFormGroup({
         accountId: new UntypedFormControl(null),
         all: new UntypedFormControl(false),
         extensions: new UntypedFormControl([], Validators.required),
      });
      const isDashboard = this.router.url.includes('/dashboard');
      if (isDashboard) {
         this.selectedAccountService
            .getSelectedAccount()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((account) => {
               this.resetSelections();
               this.selectedAccountId = account?.accountId ?? null;
               this.form.controls.accountId.setValue(this.selectedAccountId);

               if (this.selectedAccountId) {
                  this.store.dispatch(
                     extensionLoadQueuesActions.load({
                        additionalFields: [],
                        filterOverrides: [
                           `accountId = ${this.selectedAccountId}`,
                           `type = ${ExtensionTypes.CallQueue.toString()}`,
                           `isArchived = false`,
                        ],
                     })
                  );
               } else {
                  this.store.dispatch(
                     extensionLoadQueuesActions.load({
                        additionalFields: [],
                        filterOverrides: [
                           `accountId IS NULL`,
                           `type = ${ExtensionTypes.CallQueue.toString()}`,
                           `isArchived = false`,
                        ],
                     })
                  );
               }
            });
      } else {
         this.store
            .select(getSelectedAccountId)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((selectedAccountId) => {
               this.resetSelections();
               if (selectedAccountId) {
                  this.selectedAccountId = selectedAccountId;
                  this.form?.controls?.accountId?.setValue(this.selectedAccountId);
                  this.store.dispatch(
                     extensionLoadQueuesActions.load({
                        additionalFields: [],
                        filterOverrides: [
                           `accountId = ${this.selectedAccountId}`,
                           `type = ${ExtensionTypes.CallQueue.toString()}`,
                           `isArchived = false`,
                        ],
                     })
                  );
               }
            });
      }
   }

   initializeSignals() {
      this.updateCallQueueExtensionItemsSignal();
      this.updateAllSignal();
      setTimeout(() => this.cd.markForCheck(), 0); //Force form to display required *
   }

   private resetSelections() {
      this.form.controls.extensions.setValue([]);
      this.form.controls.all.setValue(false);
      this.selectedExtensions.set([]);
      this.updateCallQueueExtensionItemsSignal();
      this.updateAllSignal();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onInit(e: LcsAdvancedMultiSelectStateInitEventPayload<ExtensionOption>) {
      this.initializeSignals();
      this.form?.controls?.accountId?.setValue(this.selectedAccountId);
      const items = this.extensionItemsFromControl() ?? [];
      this.selectedExtensions.set(Array.isArray(items) ? items : []);
   }

   onAllChange(all: boolean) {
      this.form.controls.all.setValue(all);
   }

   onOpen(e: LcsAdvancedMultiSelectOpenDialogEvent<ExtensionOption>) {
      this.store.dispatch(extensionLoadQueuesActions.searchReset());
   }

   onSave(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['save']) {
      const selectedItems = e.state.selectedItems();
      this.form.controls.extensions.setValue(selectedItems);
      this.triggerConfig().group?.markAsDirty();
      this.triggerConfig().group?.markAsTouched();
   }

   onSearch(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['search']) {
      const searchText = e.searchText.replace(/[()-]/g, '');
      this.store.dispatch(extensionLoadQueuesActions.search({ searchTerm: searchText }));
   }

   onSelectedItemsChange(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['selectedItemsChange']) {
      this.selectedExtensions.set(Array.isArray(e.state.selectedItems()) ? e.state.selectedItems() : []);
   }

   onClose(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['close']) {
      const datasource = this.dataSource();
      if (datasource) {
         e.actions.datasourceChange({ datasource });
      }
      const items = this.extensionItemsFromControl() ?? [];
      this.selectedExtensions.set(Array.isArray(items) ? items : []);
   }

   isExtensionSelected(selectedExtensions: ExtensionOption[], extension: ExtensionOption) {
      return selectedExtensions.some((e) => this.extensionsTrackBy(e) === this.extensionsTrackBy(extension));
   }

   private extensionsTrackBy = (extension: ExtensionOption) => extension.extensionId;
}
